import type { AxiosError } from 'axios';
import { camelToSnake, snakeToCamel } from 'libs/stringUtilities';
import type { NextRouter } from 'next/router';
import type { FieldError } from 'react-hook-form';
import { toast } from 'react-toastify';
import type {
  FieldValidationError,
  FormValidationError,
} from 'types/cloud_api_error_type';
import {
  isValidApiError,
  isValidFormValidationError,
} from 'types/cloud_api_error_type';
import { postSignInRefresh } from 'apis/sign_in/refresh';
import { signInPage } from 'pages/pages';
import { isAxiosError } from './appAxios';

const httpErrorMessage = (error: AxiosError): string => {
  if (
    isAxiosError(error) &&
    error.response &&
    isValidApiError(error.response.data) &&
    error.response.data.message
  )
    return error.response.data.message;

  return '';
};

export const hasHttpErrorMessage = (error: AxiosError): boolean => {
  return httpErrorMessage(error).length > 0;
};

export const showHttpError = (error: AxiosError) => {
  if (hasHttpErrorMessage(error)) {
    toast.error(httpErrorMessage(error), { delay: 500 });
  } else {
    toast.error(error.message, { delay: 500 });
  }
};

export const showHttpWarn = (error: AxiosError) => {
  if (hasHttpErrorMessage(error)) {
    toast.warn(httpErrorMessage(error), { delay: 500 });
  } else {
    toast.warn(error.message, { delay: 500 });
  }
};

type HttpErrorHandlerOptionsType = Partial<{
  noToastMessage: boolean;
  noRedirection: boolean;
  authErrorPath: string | null;
  notFoundPath: string | null;
}>;

/* eslint-disable sonarjs/cognitive-complexity */
export const httpErrorHandler = (
  router: NextRouter | null,
  error: AxiosError,
  options: HttpErrorHandlerOptionsType = {
    noToastMessage: false,
    noRedirection: false,
    authErrorPath: null,
    notFoundPath: null,
  }
) => {
  if (!router?.isReady) return;

  if (
    isAxiosError(error) &&
    error.response &&
    isValidApiError(error.response.data)
  ) {
    switch (error.response.status) {
      case 400:
        if (!options.noToastMessage) showHttpError(error);
        // no redirection
        return;
      case 401:
        postSignInRefresh()
          .then(() => {
            router.push(router.asPath);
          })
          .catch(() => {
            if (!options.noToastMessage) showHttpWarn(error);
            if (!options.noRedirection)
              router.push(options.authErrorPath || signInPage.path);
          });
        return;
      case 403:
        if (!options.noToastMessage) showHttpError(error);
        if (!options.noRedirection) router.push('/403');
        return;
      case 404:
        if (!options.noToastMessage) showHttpWarn(error);
        if (!options.noRedirection) router.push(options.notFoundPath || '/404');
        return;
      case 500:
        showHttpError(error);
        router.push('/500');
    }
  }

  throw error;
};
/* eslint-enable sonarjs/cognitive-complexity */

export const getFieldValidationError = (
  field: string,
  formError: FormValidationError
): FieldValidationError | undefined => {
  if (!isValidFormValidationError(formError)) return;

  switch (formError.errorType) {
    case 'error':
      return undefined;
    case 'form_error':
      return formError.details
        ? formError.details.find(
            (fieldError) => fieldError?.field === camelToSnake(field)
          )
        : undefined;
    case 'multiple_form_error': {
      const fieldNameArray = field.split('.');
      const errorFieldGroup = formError.multipleDetails?.find(
        (detail) => detail.index === Number(fieldNameArray[1])
      );
      const errorField = errorFieldGroup?.details.find(
        (detail) => detail.field === camelToSnake(fieldNameArray[2])
      );
      return errorField
        ? {
            field,
            message: errorField?.message as string,
          }
        : undefined;
    }
  }
};

export const hasFieldValidationError = (
  field: string,
  formError: FormValidationError
): boolean => {
  if (!isValidFormValidationError(formError)) return false;

  return getFieldValidationError(field, formError) != undefined;
};

export const getFirstErrorField = <T>(
  formError: FormValidationError,
  fieldGroupName?: string
): keyof T | undefined => {
  switch (formError.errorType) {
    case 'error':
      return undefined;
    case 'form_error':
      return formError.details
        ? (snakeToCamel(formError.details[0].field) as keyof T)
        : undefined;
    case 'multiple_form_error':
      if (formError.multipleDetails && fieldGroupName) {
        const errorFieldName = snakeToCamel(
          formError.multipleDetails[0].details[0].field
        );
        return `${fieldGroupName}.${formError.multipleDetails[0].index}.${errorFieldName}` as keyof T;
      } else {
        return undefined;
      }
  }
};

export const removeFieldValidationError = (
  field: string,
  formError: FormValidationError
) => {
  if (!isValidFormValidationError(formError)) return;

  if (Array.isArray(formError?.details)) {
    const errorFieldIndex = formError.details.findIndex(
      (fieldError) => fieldError?.field === camelToSnake(field)
    );
    if (errorFieldIndex < 0) return;
    formError.details.splice(errorFieldIndex, 1);
  } else if (Array.isArray(formError.multipleDetails)) {
    const fieldNameArray = field.split('.');
    const errorFieldGroupIndex = formError.multipleDetails?.findIndex(
      (detail) => detail.index === Number(fieldNameArray[1])
    );
    if (errorFieldGroupIndex < 0) return;
    const errorFieldIndex = formError.multipleDetails[
      errorFieldGroupIndex
    ]?.details.findIndex(
      (detail) => detail.field === camelToSnake(fieldNameArray[2])
    );
    if (errorFieldIndex < 0) return;

    formError.multipleDetails[errorFieldGroupIndex]?.details.splice(
      errorFieldIndex,
      1
    );
  }
};

export const fieldState = (
  isFormErrorOccurred: boolean,
  field: string,
  formValidationError: FormValidationError,
  fieldError: FieldError | undefined
): 'invalid' | undefined => {
  if (
    isFormErrorOccurred ||
    hasFieldValidationError(field, formValidationError) ||
    fieldError
  )
    return 'invalid';

  return undefined;
};
